import { Spotlight } from "@mantine/spotlight";
import { IconCalendar, IconSearch } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useEventListQuery, useEventQuery, useResolvedEvent } from "@vantage/core";
import { EventCardBackground } from "../../../content/event/card/EventCardBackground";
import { Box, Loader, Paper } from "@mantine/core";
import { useActionsStore, type Action } from "./useActionsStore";
import { useTranslations } from "../../../../stores/useLocaleStore";
import { ResolvedEventContext } from "@vantage/core";
import { trynull } from "../../../../lib/util/trynull";
import { EVENT_REDIRECTOR_URL } from "../../../../constants";

export const VantageSpotlight = () => {
	const [query, setQuery] = useState("");
	const navigate = useNavigate();
	const providedActions = useActionsStore(state => state.actions);

	const { events: searchResults } = useEventListQuery({
		limit: 10,
		search: query,
		enabled: query.length > 0,
	});

	const actions: Action[] = [];
	actions.push(...Object.values(providedActions));

	const filteredActions = actions
		.filter(props => props.label?.toLowerCase().includes(query.toLowerCase()));

	const asUrl = trynull(() => new URL(query));

	const maybeFromRedirector = asUrl && query.startsWith(EVENT_REDIRECTOR_URL) ? asUrl.searchParams.get("url") || asUrl.searchParams.get("at") : null;

	if (!!maybeFromRedirector)
		filteredActions.push({
			label: "View Event",
			icon: <IconCalendar />,
			execute: () => navigate({
				to: "/event",
				search: { source: maybeFromRedirector },
			}),
		});
	else if (query.startsWith("http://") || query.startsWith("https://") || query.startsWith("at://"))
		filteredActions.push({
			label: "View Event",
			icon: <IconCalendar />,
			execute: () => navigate({
				to: "/event",
				search: { source: query },
			}),
		});

	const categorizedActions = filteredActions
		.reduce((acc, cur) => ({
			...acc,
			[cur.category || ""]: [...acc[cur.category || ""] || [], cur],
		}), {} as Record<string, Action[]>);

	const elements: ReactNode[] = Object.entries(categorizedActions).map(([category, actions]) => (
		<Spotlight.ActionsGroup key={category} label={category}>
			{actions.map((action, index) => (
				<Spotlight.Action
					key={index}
					onClick={action.execute}
					leftSection={action.icon}
					label={action.label}
				/>
			))}
		</Spotlight.ActionsGroup>
	));

	if (!!searchResults.length)
		elements.push(
			<Spotlight.ActionsGroup label="Events">
				{searchResults.map((query) => (
					<ResolvedEventContext value={query.data ?? null}>
						<SpotlightEventAction />
					</ResolvedEventContext>
				))}
			</Spotlight.ActionsGroup>
		);

	return (
		<Spotlight.Root
			query={query}
			onQueryChange={setQuery}
			triggerOnContentEditable
			tagsToIgnore={[]}
			shortcut={[
				"mod + K",
				"F1",
				"mod + shift + P",
			]}
			styles={{
				inner: {
					padding: "2rem",
					overflow: "scroll",
				},
				content: {
					overflow: "visible",
					borderRadius: "var(--mantine-radius-md)",
				},
			}}
		>
			<Box pos="relative">
				<Paper
					shadow="xl"
					withBorder
					radius="md"
					style={{
						position: "sticky",
						top: 0,
						zIndex: 1,
					}}
				>
					<Spotlight.Search
						placeholder="Search..."
						leftSection={<IconSearch size={16} />}
					/>
				</Paper>
				<Spotlight.ActionsList
					styles={{
						actionsList: {
							border: "none",
						},
					}}
				>
					{elements.length > 0 ? elements : <Spotlight.Empty>Nothing found...</Spotlight.Empty>}
				</Spotlight.ActionsList>
			</Box>
		</Spotlight.Root>
	);
};

export const SpotlightEventAction = () => {
	const { data, id } = useResolvedEvent();
	const navigate = useNavigate();
	const t = useTranslations();

	const label = t(data?.name);

	return (
		<Box pos="relative" style={{ overflow: "hidden" }}>
			<EventCardBackground backgroundOpacity={0.5} />
			<Spotlight.Action
				label={label || "Loading..."}
				description="Event"
				leftSection={<IconCalendar />}
				onClick={() => navigate({
					to: "/event",
					search: { id },
				})}
				pos="relative"
			/>
		</Box>
	);
};
